import React, { useState, useEffect } from 'react';
import { View, StyleSheet, Alert, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import { getCityBounds } from '@/services/maps';
import {
  downloadOfflineMap,
  deleteOfflineMap,
  getDownloadedRegions,
  findDownloadedRegionForCity,
  getDownloadProgress,
  formatFileSize,
  type OfflineMapRegion,
  type DownloadProgress,
} from '@/services/offlineMaps';
import { networkMonitor } from '@/services/networkMonitor';

type OfflineDownloadCardProps = {
  city: string;
  onDownload?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  isDownloaded?: boolean;
  downloadProgress?: number;
};

export const OfflineDownloadCard: React.FC<OfflineDownloadCardProps> = ({
  city,
  onDownload,
  onDelete,
  isDownloaded: propIsDownloaded = false,
  downloadProgress: propDownloadProgress,
}) => {
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(propIsDownloaded);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [regionId, setRegionId] = useState<string | null>(null);

  // Check if region is downloaded on mount and when connectivity returns.
  useEffect(() => {
    const checkDownloadStatus = async () => {
      if (Platform.OS === 'web') return;
      
      try {
        const regions = await getDownloadedRegions();
        const region = findDownloadedRegionForCity(regions, city);
        
        if (region) {
          setIsDownloaded(true);
          setRegionId(region.id);
        } else {
          setIsDownloaded(false);
          setRegionId(null);
        }
      } catch (error) {
        console.error('[OfflineDownloadCard] Error checking download status:', error);
      }
    };
    
    void checkDownloadStatus();
    return networkMonitor.addListener(() => {
      void checkDownloadStatus();
    });
  }, [city]);

  // Poll for download progress if downloading
  useEffect(() => {
    if (!isDownloading || !regionId || Platform.OS === 'web') return;

    const interval = setInterval(async () => {
      const progress = await getDownloadProgress(regionId);
      if (progress) {
        setDownloadProgress(progress);
        if (progress.percentage >= 100) {
          setIsDownloading(false);
          setIsDownloaded(true);
          clearInterval(interval);
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isDownloading, regionId]);

  const handleDownload = async () => {
    if (Platform.OS === 'web') {
      Alert.alert('Not Available', 'Offline maps are only available on iOS and Android.');
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(null);

    try {
      // Get city bounds for download
      const bounds = await getCityBounds(city);
      if (!bounds) {
        Alert.alert('Error', 'Could not determine city boundaries. Please try again.');
        setIsDownloading(false);
        return;
      }

      // Get style URL
      const { getStyleURL } = await import('@/config/mapbox');
      const styleURL = getStyleURL();

      // Download the offline map
      const downloadedRegionId = await downloadOfflineMap(
        {
          name: city,
          bounds,
          minZoom: 10,
          maxZoom: 16,
          styleURL,
        },
        (progress) => {
          setDownloadProgress(progress);
        }
      );

      setRegionId(downloadedRegionId);
      setIsDownloaded(true);
      setIsDownloading(false);
      
      // Call optional callback
      if (onDownload) {
        await onDownload();
      }

      Alert.alert('Success', `Offline map for ${city} downloaded successfully!`);
    } catch (error: any) {
      console.error('[OfflineDownloadCard] Download error:', error);
      Alert.alert(
        'Download Failed',
        error?.message || 'Failed to download offline map. Please check your internet connection and try again.'
      );
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  };

  const handleDelete = async () => {
    if (Platform.OS === 'web') {
      return;
    }

    if (!regionId) {
      // Try to find region by name
      try {
        const regions = await getDownloadedRegions();
        const region = findDownloadedRegionForCity(regions, city);
        if (!region) {
          Alert.alert('Error', 'Offline map not found.');
          return;
        }
        setRegionId(region.id);
      } catch (error) {
        console.error('[OfflineDownloadCard] Error finding region:', error);
        Alert.alert('Error', 'Failed to find offline map.');
        return;
      }
    }

    Alert.alert(
      'Delete Offline Map',
      `Are you sure you want to delete the offline map for ${city}? This will free up storage space.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              if (regionId) {
                await deleteOfflineMap(regionId);
              }
              
              setIsDownloaded(false);
              setRegionId(null);
              setDownloadProgress(null);
              
              // Call optional callback
              if (onDelete) {
                await onDelete();
              }
              
              Alert.alert('Success', 'Offline map deleted.');
            } catch (error: any) {
              console.error('[OfflineDownloadCard] Delete error:', error);
              Alert.alert(
                'Delete Failed',
                error?.message || 'Failed to delete offline map. Please try again.'
              );
            }
          },
        },
      ]
    );
  };

  return (
    <Card style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Ionicons
            name={isDownloaded ? 'download' : 'download-outline'}
            size={20}
            color={theme.colors.primary}
          />
          <View style={styles.titleGroup}>
            <SubHeading style={styles.title}>Offline map</SubHeading>
            <Caption color={theme.colors.textSecondary}>{city}</Caption>
          </View>
        </View>
      </View>

      {isDownloaded ? (
        <>
          <View style={styles.statusRow}>
          <Ionicons
            name="checkmark-circle"
            size={18}
            color={theme.colors.success || '#34C759'}
          />
          <Body style={[styles.statusText, { color: theme.colors.success || '#34C759' }]}>
            Offline ready
          </Body>
          </View>
          <Button
          label="Remove download"
            variant="secondary"
            onPress={handleDelete}
            style={styles.button}
          />
        </>
      ) : (
        <>
          <Body style={styles.description}>
          Download {city} to use the map without internet.
          </Body>
          {downloadProgress && downloadProgress.percentage < 100 && (
            <View style={styles.progressContainer}>
              <View style={styles.progressBar}>
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${downloadProgress.percentage}%`,
                      backgroundColor: theme.colors.primary,
                    },
                  ]}
                />
              </View>
              <Caption color={theme.colors.textSecondary} style={styles.progressText}>
                Downloading... {Math.round(downloadProgress.percentage)}%
                {downloadProgress.requiredTileCount > 0 && (
                  <> ({downloadProgress.completedTileCount}/{downloadProgress.requiredTileCount} tiles)</>
                )}
              </Caption>
            </View>
          )}
          <Button
          label={isDownloading ? 'Downloading...' : 'Download offline map'}
            onPress={handleDownload}
            disabled={isDownloading}
            style={styles.button}
          />
        </>
      )}
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 14,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  titleGroup: {
    gap: 2,
  },
  title: {
    margin: 0,
    fontSize: 16,
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  statusText: {
    fontWeight: '600',
  },
  description: {
    marginBottom: 10,
  },
  progressContainer: {
    marginBottom: 10,
  },
  progressBar: {
    height: 8,
    backgroundColor: '#E0E0E0',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 12,
  },
  button: {
    marginTop: 4,
  },
});

